import { computed, Signal, signal, untracked } from '@angular/core';
import { LocaleDto, UserVisualStateDto, VisualDto } from 'src/app/services/api.services';
import { getInsightsPbiChartType } from 'src/app/shared/chart/charts';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { isNullOrUndefined, TimeZone } from 'src/app/shared/utils';
import { AppCurrencies, AppCurrency } from 'src/app/state/app/app.reducer';
import { DataModel, DataRelationshipMapping } from '../dataModel';
import { VisualContext } from './VisualContext';
import { VisualCustomField } from './VisualCustomField';
import { Serialize, VisualDataSerializer } from './VisualDataSerializer';
import { VisualFieldCollection } from './VisualFieldCollection';
import { VisualFieldRoleCollection } from './VisualFieldRoleCollection';
import { VisualFilter } from './VisualFilter';
import { VisualHost } from './VisualHost';
import { VisualStyle } from './VisualStyle';
import { VisualType } from './VisualType';
import { VisualInteractions } from './VisualInteractions';

export class Visual<
  TOptions extends Visual.Options = Visual.Options,
  TState   extends Visual.State   = Visual.State,
  TStyle   extends VisualStyle    = VisualStyle> {
  constructor(private readonly _init: Visual.Init<TOptions, TState, TStyle>) { }

  readonly context = this._init.context;
  readonly type = this._init.type;

  clone(): Visual {
    let clone = new Visual({
      context: this.context,
      type: this.type,

      options: this.options(),
      state: this.state(),
      style: this.style(),
    });

    for (let f of this.fields.all()) {
      clone.fields.add(f.role().type, f.clone());
    }

    clone.filters.set(this.filters().map(f => new VisualFilter({
      predicate: VisualFilter.Predicate.fromFilterDto(f.createFilterDto(), t => clone.fields.find(t)?.target),
      disabled: f.disabled()
    })));

    return clone;
  }

  /**
   * Note - This property should not be directly assigned.
   * Options assignment is managed by {@link VisualHostCompnent}.
   * Gets options of this visual. */
  readonly options: Signal<TOptions> = signal(this._init.options);

  /** Gets or sets state of this visual. */
  readonly state = signal(this._init.state);

  /** Gets or sets style of this visual. */
  readonly style = signal(this._init.style);


  /** Indicates whether the visual is valid, e.g. has all necessary field roles assigned. */
  readonly isValid = computed(() =>
    !this.fields.isEmpty() &&
    !this.fieldRoles.value.filter(r => !r.isValid()).length);

  /** Gets the collection of field roles associated wit this visual. */
  readonly fieldRoles = new VisualFieldRoleCollection(this);

  /** Gets the effective collection of fields this visual has defined and is referencing. */
  readonly fields = new VisualFieldCollection(this);

  /** Gets the field sorting of this visual. */
  readonly fieldSorting = computed(() => this.type.fieldSorting(this));

  /**
   * Gets the effective collection of filters this visual has defined.
   * Note, not all filters must be contextually relevant to the visual. */
  readonly filters = signal<ReadonlyArray<VisualFilter>>([]);

  /**
   * Gets the effective locale for this visual, i.e.
   * Either state's locale, or contextual locale. */
  readonly locale = computed(() => this.context.locale() ?? this.state().locale());

  /**
   * Gets the effective time zone for this visual, i.e.
   * Either state's time zone, or contextual time zone. */
  readonly tz = computed(() => this.context.tz() ?? this.state().tz());

  /**
   * Gets the effective currency for this visual, i.e.
   * Either state's currency, or contextual currency. */
  readonly currency = computed(() => {
    let currency = this.context.currency() ?? this.state().currency();
    return this.context.currencies().getEffectiveCurrency(currency?.currencyCode);
  });

  readonly percentageDisplayChart = computed(() => this.type.percentageDisplayChart(this));
}

export namespace Visual {
  export interface Init<TOptions, TState, TStyle> {
    readonly context: VisualContext;
    readonly type: VisualType<TOptions>;

    readonly options: TOptions;
    readonly state: TState;
    readonly style: TStyle;
  }

  export class State {
    /**
     * Locale to display the visual data with.
     * When unset, contextual locale should be used see VisualContext. */
    @Serialize<LocaleDto, string>(s => ({
      read: x => s.context.getLocale(x),
      write: x => x?.id,
    }))
    readonly locale = signal<Readonly<LocaleDto> | null>(null);

    /**
     * TimeZone this visual's data is to displayed in.
     * When unset, contextual time zone should be used see VisualContext. */
    @Serialize<TimeZone, string>(s => ({
      read: x => s.context.getTimeZone(x),
      write: x => x?.id,
    }))
    readonly tz = signal<Readonly<TimeZone> | null>(null);

    /**
     * Currency to display currency values in.
     * When null, should display currencies in base currency. */
    @Serialize<AppCurrency, string>(s => ({
      read: x => s.context.getCurrency(x),
      write: x => x?.currencyCode,
    }))
    readonly currency = signal<Readonly<AppCurrency>>(null);


    /** 
     * Called to capture current state before serialization.
     * The instance this is called for may be different than the visual's own state. */
    capture(host: VisualHost) {
      let context = host.visual().context;
      this.locale.set(context.locale());
      this.tz.set(context.tz());
      this.currency.set(context.currency());
    }

    /** Serializes/deserializes the state. */
    serialize(s: VisualDataSerializer): void {
      s.serializeAnnotated(this);
    }


    setFromUserVisualStateDto(state: Readonly<UserVisualStateDto>) {
    }

    copyToUserVisualStateDto(state: Readonly<UserVisualStateDto>): UserVisualStateDto {
      return state;
    }
  }


  export interface Options {
    readonly showRowCount?: boolean;
  }

  export function createFromDto(
    visualDto: VisualDto, 
    options: {
      currencies: AppCurrencies | Signal<AppCurrencies>;
      dataModel: DataModel;
      interactions: VisualInteractions;
      enableAllFilters?: boolean;
      visualOptions?: Partial<Visual.Options>,
      userVisualState?: UserVisualStateDto,
      locale?: LocaleDto | Signal<LocaleDto>,
    }): Visual {
    let { currencies, dataModel, enableAllFilters, visualOptions, interactions, locale } = options;

    let context = VisualContext.forVisual(visualDto, {
      currencies,
      dataModel,
      interactions,
      locale,
    });

    let contentDto = visualDto.content;

    let pbiChartType = getInsightsPbiChartType(contentDto.type);
    let visualType = pbiChartType.visualType;
    if (!visualType)
      throw new Error(`Unrecognized chart type '${contentDto.type}'`);
 
    visualOptions = {
      ...deepClone(pbiChartType.visualTypeOptions.visualOptions ?? pbiChartType.visualType.visualOptions ?? {}),
      ...visualOptions,
    };
      
    let state = <Visual.State>visualType.createState(context, visualOptions);
    let style = <VisualStyle>visualType.createStyle(context, visualOptions);

    if (visualDto.content.state)
      state.serialize(new VisualDataSerializer(context, JSON.parse(visualDto.content.state), true));

    if (visualDto.content.style)
      style.serialize(new VisualDataSerializer(context, JSON.parse(visualDto.content.style), true));

    state.setFromUserVisualStateDto(options?.userVisualState);

    let visual = new Visual({
      context,
      type: visualType,
      options: visualOptions,
      state: state,
      style: style,
    });
    
    visual.fields.addFromContentDto(visualDto.content);

    visual.filters.set(contentDto.filters.map(f => new VisualFilter({
      predicate: VisualFilter.Predicate.fromFilterDto(f, context.visualFieldTargetResolver().find),
      disabled: enableAllFilters ? false  : (isNullOrUndefined(f.enabled) ? false : !!f.enabled),
      collapsed: false,
    })));

    return visual;
  }
}
